import { elementVisibility } from '@/components/rendering/elementVisibility';
import { formatCellValue } from '@/components/exportTable/exportTableToExcel';
import type { HeaderRow } from './createXlsxWorkbookBlob';
import { UIElementType } from '#types/System.types';
import type { Fields, FieldType, UIElement, UIField } from '#types/System.types';
import { defaultTimeZone } from '#types/Client.Parameters.types';

type Params = {
  formNumber?: string;
  orgName?: string;
  orgEmail?: string;
  orgPhone?: string;
  layoutStructure: UIElement;
  fields: Fields;
  fieldTypes: Record<string, FieldType>;
  visibilityMode?: string;
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: defaultTimeZone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: defaultTimeZone,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: defaultTimeZone,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const formatDate = (date: Date): string => {
  const dateOnly = dateFormatter.format(date);

  const parts = partsFormatter.formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  const second = Number(parts.find((p) => p.type === 'second')?.value ?? '0');

  if (hour === 0 && minute === 0 && second === 0) return dateOnly;
  return `${dateOnly} ${timeFormatter.format(date)}`;
};

const traverseVisibleFieldIds = (root: UIElement, isVisible: (el: UIElement) => boolean): string[] => {
  const result: string[] = [];

  const visit = (node: UIElement) => {
    if (!isVisible(node)) return;

    const el = node as Record<string, unknown>;
    if (el.uiElementType === UIElementType.Field) {
      const uiField = node as unknown as UIField;
      if (uiField.fieldId) {
        result.push(uiField.fieldId);
      }
    }

    if ('children' in el && Array.isArray(el.children)) {
      for (const child of el.children as UIElement[]) {
        visit(child);
      }
    }
  };

  visit(root);
  return result;
};

function createFormEmailHeaderRows(params: Params): HeaderRow[] {
  const contactLine = params.orgEmail && params.orgPhone
    ? `${params.orgEmail} | ${params.orgPhone}`
    : params.orgEmail || params.orgPhone || '';

  const rows: HeaderRow[] = [
    { label: 'Form Number', value: params.formNumber ?? '' },
    { label: 'Organization', value: params.orgName || 'Syncretic' },
  ];

  if (contactLine) {
    rows.push({ label: 'Contact', value: contactLine });
  }

  rows.push({ label: '', value: '' });
  rows.push({ label: 'Powered by Syncretic', value: '' });
  rows.push({ label: 'https://syncretic.work', value: '' });
  rows.push({ label: '', value: '' });

  const isVisible = (uiElement: UIElement) => {
    return elementVisibility(
      uiElement,
      params.visibilityMode,
    );
  };

  const fieldIds = traverseVisibleFieldIds(params.layoutStructure, isVisible);

  for (const fieldId of fieldIds) {
    const fieldType = params.fieldTypes[fieldId];
    const label = fieldType?.description?.shortLabel || fieldId;
    const value = formFieldValueForExportSummaryWithFieldType(params.fields[fieldId]?.value, fieldType);
    rows.push({ label, value });
  }

  return rows;
}

export default createFormEmailHeaderRows;

function formFieldValueForExportSummaryWithFieldType(value: unknown, fieldType: FieldType | undefined): string {
  if (value === null || value === undefined) return '';
  const formatted = formatCellValue(value, fieldType);
  if (formatted === null || formatted === undefined) return '';
  if (formatted instanceof Date) return formatDate(formatted);
  return String(formatted);
}

